/**
 * CodeFxStep.jsx
 * 체험형 레슨 스텝: 코드 변수 수정 → 실시간 시각 변화
 *
 * Refactored to use Universal Runner (Pyodide) for Python variable extraction.
 * Fallback to Regex for other languages or if execution fails.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import MonacoCodeEditor from '@components/editor/MonacoCodeEditor';
import ControlBar from '../ControlBar';
import { getVisualTemplate, normalizeValues } from './visualTemplates';
import { executeCode, isRunnerReady } from '../../../services/runner';

function safeRegexExtract(code, regex, defaultVal) {
  try {
    const r = new RegExp(regex, 'g');
    const matches = Array.from(code.matchAll(r));
    if (matches.length > 0) {
      // Get the last match (final re-assignment)
      const lastMatch = matches[matches.length - 1];
      // lastMatch[1] is the first capture group, fallback to lastMatch[0] (full match)
      return lastMatch[1] !== undefined ? lastMatch[1] : lastMatch[0];
    }
    return defaultVal;
  } catch {
    return defaultVal;
  }
}

function CodeFxStep({ step, onBack, onNext, canGoBack }) {
  const [code, setCode] = useState(step.initialCode || '# 코드를 수정해보세요');
  const language = (step.language || 'python').toLowerCase();

  // Real-time extracted values
  const [extractedValues, setExtractedValues] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const debounceTimer = useRef(null);

  // [Fix] Step 변경 시 코드 및 상태 초기화 (데이터 영속성 문제 해결)
  useEffect(() => {
    const initialTmpl = step.initialCode || '# 코드를 수정해보세요';
    setCode(initialTmpl);

    // 초기 추출 (Regex) - 即時 반영
    const initialValues = {};
    for (const ext of (step.extractions || [])) {
      initialValues[ext.name] = safeRegexExtract(initialTmpl, ext.regex, ext.default || '');
    }
    setExtractedValues(initialValues);
    setIsRunning(false);

    // [Fix] 마운트 시 Pyodide/JS 실행하여 정확한 초기 상태 표시
    // Regex만으로는 리스트 등 복합 자료형을 올바르게 추출할 수 없음
    const timer = setTimeout(() => {
      updateVariables(initialTmpl);
    }, 300);
    return () => clearTimeout(timer);
  }, [step.initialCode, step.extractions]);

  // Check Runner Readiness
  useEffect(() => {
    if (language === 'python' && !isRunnerReady('python')) {
      // Trigger a warm-up if needed, or just wait
      // For now, we rely on the debounce effect below
    }
  }, [language]);

  // Execute & Extract Logic
  const updateVariables = async (currentCode) => {
    // 1. Python: Use Pyodide for real execution + variable extraction
    if (language === 'python') {
      setIsRunning(true);
      try {
        const { variables, error } = await executeCode('python', currentCode);

        if (!error && variables) {
          const newValues = {};

          if (step.visual_config?.watch_variables) {
            // watch_variables로 Pyodide 변수를 가져오되, extractions가 있으면
            // 템플릿이 기대하는 이름(ext.name)으로 매핑
            const watchVars = step.visual_config.watch_variables;
            const extractions = step.extractions || [];
            watchVars.forEach((varName, idx) => {
              if (variables[varName]) {
                // extractions가 있으면 해당 인덱스의 extraction name으로 매핑
                const mappedName = extractions[idx]?.name || varName;
                newValues[mappedName] = variables[varName].value;
              }
            });
          } else if (step.extractions) {
            step.extractions.forEach(ext => {
              if (variables[ext.name]) {
                newValues[ext.name] = variables[ext.name].value;
              } else {
                // ext.name(템플릿 키)과 실제 Python 변수명이 다를 수 있음
                // regex에서 변수명을 추출하여 Pyodide 결과에서 찾기
                const varNameMatch = ext.regex?.match?.(/^(\w+)/);
                const actualVarName = varNameMatch?.[1];
                if (actualVarName && variables[actualVarName]) {
                  newValues[ext.name] = variables[actualVarName].value;
                } else {
                  newValues[ext.name] = safeRegexExtract(currentCode, ext.regex, ext.default);
                }
              }
            });
          } else {
            Object.keys(variables).forEach(k => {
              const v = variables[k];
              if (v.type !== 'DataFrame' && v.type !== 'module') {
                newValues[k] = v.value;
              }
            });
          }

          setExtractedValues(newValues);
        }
      } catch (e) {
        console.error("Pyodide Error:", e);
      } finally {
        setIsRunning(false);
      }
    }
    // 2. JavaScript: Execute in sandbox + extract variables
    else if (language === 'javascript' || language === 'js') {
      setIsRunning(true);
      try {
        const { variables, error } = await executeCode('javascript', currentCode);

        if (!error && variables && Object.keys(variables).length > 0) {
          const newValues = {};
          if (step.extractions) {
            step.extractions.forEach(ext => {
              if (variables[ext.name] !== undefined) {
                newValues[ext.name] = variables[ext.name];
              } else {
                newValues[ext.name] = safeRegexExtract(currentCode, ext.regex, ext.default || '');
              }
            });
          } else {
            Object.assign(newValues, variables);
          }
          setExtractedValues(newValues);
        } else {
          // Fallback to regex if execution failed or no variables
          const newValues = {};
          for (const ext of (step.extractions || [])) {
            newValues[ext.name] = safeRegexExtract(currentCode, ext.regex, ext.default || '');
          }
          setExtractedValues(newValues);
        }
      } catch {
        // Regex fallback on error
        const newValues = {};
        for (const ext of (step.extractions || [])) {
          newValues[ext.name] = safeRegexExtract(currentCode, ext.regex, ext.default || '');
        }
        setExtractedValues(newValues);
      } finally {
        setIsRunning(false);
      }
    }
    // 3. CSS/HTML/Other: Regex-only extraction (no execution needed)
    else {
      const newValues = {};
      for (const ext of (step.extractions || [])) {
        newValues[ext.name] = safeRegexExtract(currentCode, ext.regex, ext.default || '');
      }
      setExtractedValues(newValues);
    }
  };

  const handleCodeChange = (newCode) => {
    setCode(newCode);

    // Immediate Regex Update (for responsiveness)
    if (language !== 'python') {
      const quickValues = {};
      for (const ext of (step.extractions || [])) {
        quickValues[ext.name] = safeRegexExtract(newCode, ext.regex, ext.default || '');
      }
      setExtractedValues(quickValues);
    }

    // Debounced Execution
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      updateVariables(newCode);
    }, 800);
  };

  const VisualTemplate = useMemo(
    () => getVisualTemplate(step.visual_template),
    [step.visual_template]
  );

  return (
    <div className="relative h-full flex flex-col">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-slate-800/50">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-[10px] font-bold rounded uppercase tracking-wider">
            Code FX ({language === 'python' ? 'Pyodide' : language === 'javascript' || language === 'js' ? 'JS Sandbox' : 'Regex'})
          </span>
        </div>
        <h3 className="text-lg font-bold text-white mb-1">{step.title || '코드 FX'}</h3>
        {step.text && <p className="text-sm text-slate-400 leading-relaxed">{step.text}</p>}
      </div>

      {/* Split View: Editor + Visual */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Code Editor */}
        <div className="w-1/2 flex flex-col border-r border-slate-800 min-h-0">
          <div className="flex-1 min-h-0 relative">
            <MonacoCodeEditor
              initialCode={step.initialCode || ''}
              language={language}
              onChange={handleCodeChange}
              height="100%"
              className="h-full flex flex-col"
            />
            {isRunning && (
              <div className="absolute top-2 right-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Visual Template */}
        <div className="w-1/2 bg-[#020617] relative overflow-hidden min-h-0">
          <VisualTemplate
            values={normalizeValues(step.visual_template, extractedValues)}
            config={step.visual_config || {}}
            code={code}
          />
        </div>
      </div>

      <ControlBar onBack={onBack} onNext={onNext} canGoBack={canGoBack} />
    </div>
  );
}

export default CodeFxStep;
